/**
 * Document Generator Service
 * Generates PDF documents (invoices, receipts, statements) using templates
 */

import puppeteer from 'puppeteer';
import Handlebars from 'handlebars';
import { v4 as uuidv4 } from 'uuid';
import type {
  DocumentTemplate,
  GeneratedDocument,
  DeliveryMethod,
  ExecutionContext,
} from '../types';

export class DocumentGeneratorService {
  private db: any;
  private emailService: any;
  private storageBasePath: string;

  constructor(db: any, emailService?: any, storageBasePath: string = './uploads/documents') {
    this.db = db;
    this.emailService = emailService;
    this.storageBasePath = storageBasePath;

    // Register Handlebars helpers
    this.registerHandlebarsHelpers();
  }

  /**
   * Register custom Handlebars helpers for templates
   */
  private registerHandlebarsHelpers(): void {
    // Format number with commas
    Handlebars.registerHelper('formatNumber', (num: number) => {
      return num?.toLocaleString('ar-SA') || '0';
    });

    // Format currency
    Handlebars.registerHelper('formatCurrency', (amount: number, currency: string = 'SAR') => {
      return new Intl.NumberFormat('ar-SA', {
        style: 'currency',
        currency,
      }).format(amount || 0);
    });

    // Format date
    Handlebars.registerHelper('formatDate', (date: Date | string, format?: string) => {
      const d = new Date(date);
      return d.toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    });

    // Conditional helper
    Handlebars.registerHelper('ifEquals', function(this: any, a: any, b: any, options: any) {
      return a === b ? options.fn(this) : options.inverse(this);
    });

    // Sum array of numbers
    Handlebars.registerHelper('sum', (items: any[], key: string) => {
      return items?.reduce((acc, item) => acc + (item[key] || 0), 0) || 0;
    });

    // Loop with index
    Handlebars.registerHelper('eachWithIndex', function(this: any, arr: any[], options: any) {
      let result = '';
      arr?.forEach((item, index) => {
        result += options.fn({ ...item, _index: index + 1 });
      });
      return result;
    });
  }

  /**
   * Generate a PDF document from a template
   */
  async generateDocument(
    templateId: string,
    data: Record<string, any>,
    context: ExecutionContext,
    options?: {
      deliveryMethod?: DeliveryMethod;
      deliveryTo?: string;
      documentNumber?: string;
    }
  ): Promise<GeneratedDocument> {
    // Get the template
    const template = await this.getTemplateById(templateId);
    if (!template) {
      throw new Error('Template not found');
    }

    // Compile the template
    const compiledHtml = this.compileTemplate(template, data);

    // Generate PDF
    const pdfBuffer = await this.generatePDF(compiledHtml, template);

    // Save the document
    const documentId = uuidv4();
    const fileName = `${template.templateType}_${options?.documentNumber || documentId}.pdf`;
    const filePath = `${this.storageBasePath}/${context.userId}/${fileName}`;

    await this.savePDFToStorage(pdfBuffer, filePath);

    // Create document record
    const document: GeneratedDocument = {
      id: documentId,
      templateId,
      userId: context.userId,
      documentNumber: options?.documentNumber,
      documentType: template.templateType,
      fileUrl: filePath,
      fileName,
      fileSize: pdfBuffer.length,
      dataSnapshot: data,
      agentId: context.agentId,
      callId: context.callId,
      deliveryStatus: 'pending',
      createdAt: new Date(),
    };

    await this.saveDocumentRecord(document);

    // Deliver if requested
    if (options?.deliveryMethod && options?.deliveryTo) {
      await this.deliverDocument(document, options.deliveryMethod, options.deliveryTo, template);
    }

    return document;
  }

  /**
   * Compile template with data
   */
  private compileTemplate(template: DocumentTemplate, data: Record<string, any>): string {
    // Compile header, body, and footer
    const headerHtml = template.headerTemplate 
      ? Handlebars.compile(template.headerTemplate)(data) 
      : '';
    
    const bodyHtml = Handlebars.compile(template.htmlTemplate)(data);
    
    const footerHtml = template.footerTemplate 
      ? Handlebars.compile(template.footerTemplate)(data) 
      : '';

    // Build complete HTML document
    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Tahoma', sans-serif;
            font-size: 12pt;
            line-height: 1.6;
            color: #333;
            direction: rtl;
          }
          ${template.cssStyles || ''}
        </style>
      </head>
      <body>
        ${headerHtml ? `<header>${headerHtml}</header>` : ''}
        <main>${bodyHtml}</main>
        ${footerHtml ? `<footer>${footerHtml}</footer>` : ''}
      </body>
      </html>
    `;
  }

  /**
   * Generate PDF from HTML using Puppeteer
   */
  private async generatePDF(html: string, template: DocumentTemplate): Promise<Buffer> {
    const browser = await puppeteer.launch({
      headless: true,
      args: ['--no-sandbox', '--disable-setuid-sandbox'],
    });

    try {
      const page = await browser.newPage();
      await page.setContent(html, { waitUntil: 'networkidle0' });

      const pdfBuffer = await page.pdf({
        format: template.paperSize as any,
        landscape: template.orientation === 'landscape',
        margin: {
          top: `${template.margins.top}mm`,
          right: `${template.margins.right}mm`,
          bottom: `${template.margins.bottom}mm`,
          left: `${template.margins.left}mm`,
        },
        printBackground: true,
      });

      return Buffer.from(pdfBuffer);
    } finally {
      await browser.close();
    }
  }

  /**
   * Deliver document via email, WhatsApp, or SMS
   */
  async deliverDocument(
    document: GeneratedDocument,
    method: DeliveryMethod,
    recipient: string,
    template?: DocumentTemplate
  ): Promise<{ success: boolean; message: string }> {
    try {
      switch (method) {
        case 'email':
          return await this.sendDocumentByEmail(document, recipient, template);
        
        case 'whatsapp':
          return await this.sendDocumentByWhatsApp(document, recipient);
        
        case 'sms':
          return await this.sendDocumentBySMS(document, recipient);
        
        case 'download':
          return { success: true, message: 'Document ready for download' };
        
        default:
          return { success: false, message: 'Unsupported delivery method' };
      }
    } catch (error: any) {
      console.error('[Document Generator] Delivery failed:', error);
      
      await this.updateDeliveryStatus(document.id, 'failed');
      
      return { success: false, message: error.message };
    }
  }

  /**
   * Send document by email
   */
  private async sendDocumentByEmail(
    document: GeneratedDocument,
    email: string,
    template?: DocumentTemplate
  ): Promise<{ success: boolean; message: string }> {
    if (!this.emailService) {
      throw new Error('Email service not configured');
    }

    const subject = template?.emailSubjectTemplate 
      ? Handlebars.compile(template.emailSubjectTemplate)(document.dataSnapshot)
      : `Document: ${document.documentNumber || document.id}`;

    const body = template?.emailBodyTemplate
      ? Handlebars.compile(template.emailBodyTemplate)(document.dataSnapshot)
      : `Please find the attached document.`;

    // Read the PDF file
    const pdfBuffer = await this.readPDFFromStorage(document.fileUrl);

    await this.emailService.send({
      to: email,
      subject,
      html: body,
      attachments: [{
        filename: document.fileName,
        content: pdfBuffer,
        contentType: 'application/pdf',
      }],
    });

    await this.updateDeliveryStatus(document.id, 'sent', email);

    return { success: true, message: `Document sent to ${email}` };
  }

  /**
   * Send document by WhatsApp
   */
  private async sendDocumentByWhatsApp(
    document: GeneratedDocument,
    phoneNumber: string
  ): Promise<{ success: boolean; message: string }> {
    // TODO: Integrate with WhatsApp API
    console.log(`[Document Generator] Would send to WhatsApp: ${phoneNumber}`);
    
    await this.updateDeliveryStatus(document.id, 'sent', phoneNumber);
    
    return { success: true, message: `Document sent to WhatsApp ${phoneNumber}` };
  }

  /**
   * Send document link by SMS
   */
  private async sendDocumentBySMS(
    document: GeneratedDocument,
    phoneNumber: string
  ): Promise<{ success: boolean; message: string }> {
    // TODO: Integrate with SMS API
    console.log(`[Document Generator] Would send SMS link to: ${phoneNumber}`);
    
    await this.updateDeliveryStatus(document.id, 'sent', phoneNumber);
    
    return { success: true, message: `Document link sent to ${phoneNumber}` };
  }

  /**
   * Get built-in invoice template
   */
  getInvoiceTemplate(): string {
    return `
      <div class="invoice">
        <div class="invoice-header">
          <div class="company-info">
            <h1>{{companyName}}</h1>
            <p>{{companyAddress}}</p>
            <p>{{companyPhone}}</p>
          </div>
          <div class="invoice-info">
            <h2>فاتورة</h2>
            <p><strong>رقم الفاتورة:</strong> {{invoiceNumber}}</p>
            <p><strong>التاريخ:</strong> {{formatDate invoiceDate}}</p>
            <p><strong>تاريخ الاستحقاق:</strong> {{formatDate dueDate}}</p>
          </div>
        </div>

        <div class="customer-info">
          <h3>معلومات العميل</h3>
          <p><strong>الاسم:</strong> {{customerName}}</p>
          <p><strong>العنوان:</strong> {{customerAddress}}</p>
          <p><strong>الهاتف:</strong> {{customerPhone}}</p>
        </div>

        <table class="invoice-items">
          <thead>
            <tr>
              <th>#</th>
              <th>الوصف</th>
              <th>الكمية</th>
              <th>السعر</th>
              <th>المجموع</th>
            </tr>
          </thead>
          <tbody>
            {{#eachWithIndex items}}
            <tr>
              <td>{{_index}}</td>
              <td>{{description}}</td>
              <td>{{quantity}}</td>
              <td>{{formatCurrency price currency}}</td>
              <td>{{formatCurrency total currency}}</td>
            </tr>
            {{/eachWithIndex}}
          </tbody>
        </table>

        <div class="invoice-totals">
          <p><strong>المجموع الفرعي:</strong> {{formatCurrency subtotal currency}}</p>
          {{#if tax}}
          <p><strong>الضريبة ({{taxRate}}%):</strong> {{formatCurrency tax currency}}</p>
          {{/if}}
          {{#if discount}}
          <p><strong>الخصم:</strong> {{formatCurrency discount currency}}</p>
          {{/if}}
          <p class="total"><strong>الإجمالي:</strong> {{formatCurrency total currency}}</p>
        </div>

        {{#if notes}}
        <div class="notes">
          <h4>ملاحظات</h4>
          <p>{{notes}}</p>
        </div>
        {{/if}}
      </div>
    `;
  }

  /**
   * Get built-in receipt template
   */
  getReceiptTemplate(): string {
    return `
      <div class="receipt">
        <div class="receipt-header">
          <h1>{{companyName}}</h1>
          <h2>إيصال دفع</h2>
          <p>رقم الإيصال: {{receiptNumber}}</p>
          <p>التاريخ: {{formatDate receiptDate}}</p>
        </div>

        <div class="receipt-body">
          <p>استلمنا من السيد/ة: <strong>{{customerName}}</strong></p>
          <p>مبلغ وقدره: <strong>{{formatCurrency amount currency}}</strong></p>
          <p>{{amountInWords}}</p>
          <p>وذلك عن: {{paymentFor}}</p>
          <p>طريقة الدفع: {{paymentMethod}}</p>
          {{#if referenceNumber}}
          <p>رقم المرجع: {{referenceNumber}}</p>
          {{/if}}
        </div>

        <div class="receipt-footer">
          <p>التوقيع: _________________</p>
          <p>التاريخ: {{formatDate receiptDate}}</p>
        </div>
      </div>
    `;
  }

  /**
   * Get built-in statement template
   */
  getStatementTemplate(): string {
    return `
      <div class="statement">
        <div class="statement-header">
          <h1>{{companyName}}</h1>
          <h2>كشف حساب</h2>
          <p>الفترة: {{formatDate fromDate}} - {{formatDate toDate}}</p>
        </div>

        <div class="customer-info">
          <p><strong>العميل:</strong> {{customerName}}</p>
          <p><strong>رقم الحساب:</strong> {{accountNumber}}</p>
        </div>

        <table class="transactions">
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>الوصف</th>
              <th>مدين</th>
              <th>دائن</th>
              <th>الرصيد</th>
            </tr>
          </thead>
          <tbody>
            <tr class="opening-balance">
              <td>{{formatDate fromDate}}</td>
              <td>الرصيد الافتتاحي</td>
              <td></td>
              <td></td>
              <td>{{formatCurrency openingBalance currency}}</td>
            </tr>
            {{#each transactions}}
            <tr>
              <td>{{formatDate date}}</td>
              <td>{{description}}</td>
              <td>{{#if debit}}{{formatCurrency debit ../currency}}{{/if}}</td>
              <td>{{#if credit}}{{formatCurrency credit ../currency}}{{/if}}</td>
              <td>{{formatCurrency balance ../currency}}</td>
            </tr>
            {{/each}}
          </tbody>
        </table>

        <div class="statement-summary">
          <p><strong>الرصيد الحالي:</strong> {{formatCurrency closingBalance currency}}</p>
        </div>
      </div>
    `;
  }

  // Database helper methods
  private async getTemplateById(templateId: string): Promise<DocumentTemplate | null> {
    console.log('[Document Generator] Getting template:', templateId);
    return null;
  }

  private async saveDocumentRecord(document: GeneratedDocument): Promise<void> {
    console.log('[Document Generator] Saving document record:', document.id);
  }

  private async updateDeliveryStatus(
    documentId: string,
    status: 'pending' | 'sent' | 'failed',
    deliveredTo?: string
  ): Promise<void> {
    console.log(`[Document Generator] Update document ${documentId} delivery status to ${status}`);
  }

  private async savePDFToStorage(buffer: Buffer, path: string): Promise<void> {
    console.log('[Document Generator] Saving PDF to:', path);
    // TODO: Implement actual file storage
  }

  private async readPDFFromStorage(path: string): Promise<Buffer> {
    console.log('[Document Generator] Reading PDF from:', path);
    return Buffer.from([]);
  }
}
